import { axiosClient } from './axiosClient'

const API_BASE_URL = (process.env.REACT_APP_NEW_BASE_V4_URL_MONITORING || 'http://localhost:3003').replace(/\/$/, '')

// Helper function to transform MongoDB _id to id
const transformMongoDocument = (doc) => {
  if (!doc) return doc

  // Handle single document
  if (doc._id) {
    const transformed = { ...doc }
    // Handle both ObjectId format { $oid: 'xxx' } and string format
    transformed.id = typeof doc._id === 'object' && doc._id.$oid ? doc._id.$oid : doc._id
    delete transformed._id
    return transformed
  }

  return doc
}

// Helper function to transform response data
const transformResponseData = (data) => {
  if (!data) return data

  // Handle array of documents
  if (Array.isArray(data)) {
    return data.map(transformMongoDocument)
  }

  // Handle nested array in data property
  if (data.data && Array.isArray(data.data)) {
    return {
      ...data,
      data: data.data.map(transformMongoDocument)
    }
  }

  // Handle single document
  return transformMongoDocument(data)
}

// Use centralized axios client directly with baseURL
// Use centralized axios client directly
const apiClient = axiosClient
// REMOVED mutation: apiClient.defaults.baseURL = API_BASE_URL

// Add response interceptor for _id to id transformation
apiClient.interceptors.response.use(
  (response) => {
    // Transform _id to id in response data
    if (response.data) {
      response.data = transformResponseData(response.data)
    }
    return response
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message)
    throw error
  }
)

// Fetch all billing records with optional filters
export const fetchBillingRecords = async (params = {}) => {
  try {
    const response = await apiClient.get(API_BASE_URL + '/billing', { params })
    return response.data
  } catch (error) {
    console.error('Error fetching billing records:', error)
    throw error
  }
}

// Create a new billing record
export const createBillingRecord = async (data) => {
  try {
    const response = await apiClient.post(API_BASE_URL + '/billing', data)
    return response.data
  } catch (error) {
    console.error('Error creating billing record:', error)
    throw error
  }
}

// Update an existing billing record
export const updateBillingRecord = async (id, data) => {
  try {
    const response = await apiClient.put(API_BASE_URL + `/billing/${id}`, data)
    return response.data
  } catch (error) {
    console.error('Error updating billing record:', error)
    throw error
  }
}

// Delete a billing record
export const deleteBillingRecord = async (id) => {
  try {
    const response = await apiClient.delete(API_BASE_URL + `/billing/${id}`)
    return response.data
  } catch (error) {
    console.error('Error deleting billing record:', error)
    throw error
  }
}

// Fetch a single billing record by ID
export const fetchBillingRecordById = async (id) => {
  try {
    const response = await apiClient.get(API_BASE_URL + `/billing/${id}`)
    return response.data
  } catch (error) {
    console.error('Error fetching billing record by ID:', error)
    throw error
  }
}

// Fetch billing records for a specific plant
export const fetchPlantBillingRecords = async (plantId, params = {}) => {
  return fetchBillingRecords({ ...params, plantId })
}

// Fetch billing summary statistics
export const fetchBillingSummary = async (plantId) => {
  try {
    const params = plantId ? { plantId } : {}
    const response = await apiClient.get(API_BASE_URL + '/billing/summary', { params })
    return response.data
  } catch (error) {
    console.error('Error fetching billing summary:', error)
    throw error
  }
}

// Fetch plants for billing form (filters out plants that already have records for the month)
export const fetchPlantsForBillingForm = async (recordDate) => {
  try {
    const params = recordDate ? { recordDate } : {}
    const response = await apiClient.get(API_BASE_URL + '/billing/form/plants', { params })
    return response.data || []
  } catch (error) {
    console.error('Error fetching plants for billing form:', error)
    return []
  }
}

// Sum 'actGeneration' from Daily Generation Reports for the month of the given recordDate
export const fetchMonthlyActualGeneration = async (plantId, recordDate) => {
  try {
    if (!plantId || !recordDate) return 0

    // Normalize recordDate to a Date
    const dateObj = typeof recordDate === 'string' || recordDate instanceof Date
      ? new Date(recordDate)
      : (recordDate && typeof recordDate.toDate === 'function')
        ? recordDate.toDate()
        : new Date(recordDate)

    const year = dateObj.getFullYear()
    const monthIndex = dateObj.getMonth() // 0-based
    const pad2 = (n) => String(n).padStart(2, '0')
    const yearMonth = `${year}-${pad2(monthIndex + 1)}`

    const response = await apiClient.get(API_BASE_URL + `/billing/dgr-sum/${plantId}`, {
      params: { recordDate: yearMonth },
    })

    const data = response.data || {}
    return Number(data.sum) || 0
  } catch (e) {
    console.error('Error fetching monthly actual generation from DGR:', e)
    return 0
  }
}

// Fetch billing record by plant and month
export const fetchBillingRecordByPlantAndMonth = async (plantId, recordDate) => {
  try {
    if (!plantId || !recordDate) {
      return {
        success: false,
        data: null,
        error: 'Plant ID and record date are required'
      }
    }

    // Convert recordDate to YYYY-MM format for filtering
    const date = new Date(recordDate)
    const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`

    const response = await apiClient.get(API_BASE_URL + '/billing', {
      params: { plantId, recordDate: yearMonth },
    })

    const result = response.data

    // Return the first matching record (should only be one per plant per month)
    if (result.data && result.data.length > 0) {
      return { success: true, data: result.data[0] }
    }

    return { success: false, data: null }
  } catch (error) {
    console.error('Error fetching billing record:', error)
    return { success: false, data: null, error: error.message }
  }
}

// Update billing record with invoice details
export const updateBillingRecordWithInvoice = async (billingRecordId, invoiceData) => {
  try {
    if (!billingRecordId) {
      throw new Error('Billing record ID is required')
    }

    // Map payment status from invoice format to billing record format
    const statusMapping = {
      'PAID': 'Paid',
      'UNPAID': 'Unpaid',
      'PENDING': 'Pending',
      'Paid': 'Paid',
      'Unpaid': 'Unpaid',
      'Pending': 'Pending',
      'To Be Raised': 'To Be Raised',
      'Draft': 'Draft'
    }

    // Format the data according to backend expectations
    const formattedData = {}

    if (invoiceData.invoiceNumber !== undefined) {
      formattedData.invoiceNumber = invoiceData.invoiceNumber
    }

    if (invoiceData.paymentStatus !== undefined) {
      formattedData.paymentStatus = statusMapping[invoiceData.paymentStatus] || invoiceData.paymentStatus
    }

    if (invoiceData.invoiceDate !== undefined) {
      const invoiceDate = new Date(invoiceData.invoiceDate)
      formattedData.invoiceDate = invoiceDate.toISOString()
    }

    if (invoiceData.dueDate !== undefined) {
      const dueDate = new Date(invoiceData.dueDate)
      formattedData.dueDate = dueDate.toISOString()
    }

    if (invoiceData.amount !== undefined) {
      formattedData.amount = parseFloat(invoiceData.amount)
    }

    if (invoiceData.currency !== undefined) {
      formattedData.currency = invoiceData.currency
    }

    console.log('Updating billing record:', billingRecordId, 'with data:', formattedData)

    const response = await apiClient.put(API_BASE_URL + `/billing/${billingRecordId}`, formattedData)

    console.log('Billing record updated successfully:', response.data)
    return { success: true, data: response.data }
  } catch (error) {
    console.error('Error updating billing record:', error)
    return { success: false, error: error.message }
  }
}

// Sync billing record from invoice changes
export const syncBillingRecordFromInvoice = async (plantId, invoiceDate, invoiceData) => {
  try {
    const invoiceMonth = new Date(invoiceDate).toISOString().split('T')[0].substring(0, 7) // Get YYYY-MM format

    const syncData = {
      plantId: plantId,
      invoiceMonth: invoiceMonth,
      actualGeneration: invoiceData.energyValue,
      deemedGeneration: invoiceData.deemedEnergy,
      tariff: invoiceData.mainTariff,
      amount: invoiceData.invoiceValue,
      paymentStatus: invoiceData.paymentStatus,
      invoiceNumber: invoiceData.invoiceNumber,
      invoiceDate: invoiceData.invoiceDate,
      dueDate: invoiceData.dueDate,
    }

    console.log('Syncing billing record from invoice:', syncData)

    const response = await apiClient.post(API_BASE_URL + '/billing/sync-from-invoice', syncData)

    console.log('Billing record synced from invoice:', response.data)
    return response.data
  } catch (error) {
    console.error('Error syncing billing record from invoice:', error)
    // Don't throw - log and continue (invoice update should succeed even if billing record sync fails)
    return { success: false, error: error.message }
  }
}

// Import billing records from Excel file
export const importBillingRecords = async (file, month) => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('month', month)

    const response = await apiClient.post(API_BASE_URL + '/billing/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return response.data
  } catch (error) {
    console.error('Error importing billing records:', error)
    throw error
  }
}

// Utility: Format currency
export const formatCurrency = (amount, currencyCode = 'INR') => {
  const formatters = {
    'INR': new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }),
    'USD': new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }),
    'EUR': new Intl.NumberFormat('en-EU', { style: 'currency', currency: 'EUR' }),
  }

  const formatter = formatters[currencyCode] || formatters['INR']
  return formatter.format(amount)
}

// Utility: Calculate total amount from records
export const calculateTotalAmount = (records) => {
  return records.reduce((total, record) => total + (record.amount || 0), 0)
}

// Utility: Get status color
export const getStatusColor = (status) => {
  const colors = {
    'PAID': 'success',
    'Paid': 'success',
    'UNPAID': 'error',
    'Unpaid': 'error',
    'PENDING': 'warning',
    'Pending': 'warning',
    'To Be Raised': 'default',
  }
  return colors[status] || 'default'
}

// Utility: Get status badge color
export const getStatusBadgeColor = (status) => {
  const colors = {
    'PAID': '#4caf50',
    'Paid': '#4caf50',
    'UNPAID': '#f44336',
    'Unpaid': '#f44336',
    'PENDING': '#ff9800',
    'Pending': '#ff9800',
    'To Be Raised': '#9e9e9e',
  }
  return colors[status] || '#9e9e9e'
}


const billingRecordsService = {
  fetchBillingRecords,
  createBillingRecord,
  updateBillingRecord,
  deleteBillingRecord,
  fetchBillingRecordById,
  fetchPlantBillingRecords,
  fetchBillingSummary,
  fetchPlantsForBillingForm,
  fetchMonthlyActualGeneration,
  fetchBillingRecordByPlantAndMonth,
  updateBillingRecordWithInvoice,
  syncBillingRecordFromInvoice,
  importBillingRecords,
  formatCurrency,
  calculateTotalAmount,
  getStatusColor,
  getStatusBadgeColor,
}

export default billingRecordsService
